import { MeasurePanel } from './MeasurePanel.js';
import {
  anticipatedErrorPercent,
  plasterVolumeErrorM3,
  plasterVolumeM3,
  volumeDecimals,
  formatExtentCm,
  surfaceI18n,
  SURFACE_VIS,
  PROFILE_REF,
  VOLUME_TOP,
  PROFILE_BANDS,
  EXTRA_PLASTER_MAX_M,
  MAX_DISTANCE_MAX_M,
  PROFILE_OFFSET_MAX_M,
  PROFILE_RANGE_MAX_M,
  PROFILE_RANGE_MIN_M,
} from '../../utils/Surface.js';
import {
  formatBandEdgeCm,
  profileBandColor,
  profileBandLegendRows,
  profileBandWeight,
  profileNormal,
  profileTiltDeg,
} from '../../utils/surfaceProfile.js';
import { Gradients } from '../../materials/Gradients.js';

function clampStepped(value, min, max, step) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const snapped = Math.round(n / step) * step;
  const clamped = Math.min(max, Math.max(min, snapped));
  const decimals = step >= 1 ? 0 : Math.min(6, (String(step).split('.')[1] || '').length);
  return Number(clamped.toFixed(decimals));
}

function formatParam(value, step) {
  if (!Number.isFinite(value)) return '';
  if (step >= 1) return String(Math.round(value));
  const decimals = Math.min(6, (String(step).split('.')[1] || '').length);
  return value.toFixed(decimals);
}

/**
 * The slider rows, keyed by the Surface field `_bindParam` writes. `i18n` is
 * the key stem (`surfaces.<i18n>` label, `surfaces.<i18n>_help` description),
 * `label` / `help` the English fallbacks, `profile` = only in height-profile mode.
 */
const SLIDER_PARAMS = {
  angleDeg: {
    i18n: 'angle',
    label: 'Angle threshold (°)',
    help: 'How much a point’s local orientation may differ from the detected plane. Lower stays on one face; higher can wrap around corners.',
  },
  maxDistance: {
    i18n: 'distance',
    label: 'Distance from plane (cm)',
    step: '0.1',
    help: 'Slab thickness around the plane: waviness and proud or hollow spots still count if they stay inside this distance.',
  },
  maxGap: {
    i18n: 'gap',
    label: 'Maximum gap (cm)',
    step: '1',
    help: 'Jump across empty scan holes of this size. Windows stay holes; only exterior gaps can be bridged.',
  },
  profileOffsetM: {
    i18n: 'profile_offset',
    label: 'Level offset (cm)',
    step: '0.1',
    profile: true,
    help: 'Shifts the zero level of the height profile along the normal, starting from the surface centroid. Display only — does not change the detected surface.',
  },
  profileRangeBelowM: {
    i18n: 'profile_range_below',
    label: 'Range below (cm)',
    step: '0.1',
    profile: true,
    help: 'How deep below the zero level (hollow side, to fill) the colour gradient reaches, with its own scale. Deeper points keep their original colour and are left out of the volume. 0 = only what stands proud. Follows the distance from plane until you change it.',
  },
  profileRangeAboveM: {
    i18n: 'profile_range_above',
    label: 'Range above (cm)',
    step: '0.1',
    profile: true,
    help: 'How high above the zero level (proud side, to chip off) the colour gradient reaches, with its own scale. Higher points keep their original colour and are left out of the volume. 0 = only the hollows, filled exactly to the zero level. Follows the distance from plane until you change it.',
  },
  extraPlasterM: {
    i18n: 'extra_plaster',
    label: 'Extra plaster width (cm)',
    step: '0.1',
    profile: true,
    help: 'Adds S × width to plaster volume for overall material demand. Does not change the grown surface.',
  },
};

/** One slider row: label + help toggle, description, −/slider/+ and a number input. */
function sliderParamHtml(key) {
  const def = SLIDER_PARAMS[key];
  const cls = def.profile ? 'surface-param surface-param--profile' : 'surface-param';
  const hidden = def.profile ? ' hidden' : '';
  const step = def.step ? ` step="${def.step}"` : '';
  return `        <div class="${cls}" data-param="${key}"${hidden}>
          <div class="surface-param-head">
            <label class="pv-select-label" data-i18n="surfaces.${def.i18n}">${def.label}</label>
            <button type="button" class="surface-param-help" aria-expanded="false" data-i18n="[title]surfaces.help_toggle">?</button>
          </div>
          <p class="surface-param-desc" hidden data-i18n="surfaces.${def.i18n}_help">
            ${def.help}
          </p>
          <div class="surface-param-row">
            <button type="button" class="surface-step" data-dir="-1" aria-label="Decrease">−</button>
            <div class="surface-slider" data-slider></div>
            <button type="button" class="surface-step" data-dir="1" aria-label="Increase">+</button>
            <input type="number" class="surface-param-input" inputmode="decimal"${step}>
          </div>
        </div>
`;
}

/** `<option>`s for the colour-resolution select; the numeric labels are language-neutral. */
function profileBandsOptionsHtml() {
  return PROFILE_BANDS.map((n) =>
    n === 0
      ? '<option value="0" data-i18n="surfaces.profile_bands_continuous">Continuous</option>'
      : `<option value="${n}">${n}</option>`
  ).join('\n            ');
}

/** Collapsible panel groups and whether they start open (grow parameters and plaster closed, display open). */
const PANEL_GROUPS = { growParams: false, display: true, plaster: false };

export class SurfacePanel extends MeasurePanel {
  /**
   * Whether a collapsible group (`growParams` / `display` / `plaster`) is open: read with
   * one argument, set with a boolean. Remembered per browser (localStorage,
   * best effort — private windows may refuse); falls back to the group's default.
   */
  static groupOpen(name, value) {
    const key = `potree.surfacePanel.${name}Open`;
    try {
      if (value === undefined) {
        const saved = localStorage.getItem(key);
        return saved === null ? !!PANEL_GROUPS[name] : saved === '1';
      }
      localStorage.setItem(key, value ? '1' : '0');
    } catch {
      /* storage unavailable → default */
    }
    return value === undefined ? !!PANEL_GROUPS[name] : !!value;
  }
  constructor(viewer, surface, propertiesPanel) {
    super(viewer, surface, propertiesPanel);
    this.surface = surface;
    this._sliders = {};
    this._paramsDirty = false;
    /** `${bands}:${rangeCm}` + gradient of the rows currently in the band legend; null = none / continuous. */
    this._bandLegendKey = null;
    this._bandLegendGradient = null;

    const removeIconPath = Potree.resourcePath + '/icons/remove.svg';
    this.elContent = $(`
      <div class="measurement_content selectable surface-panel">
        <table class="measurement_value_table surface-stats">
          <tr>
            <th data-i18n="[title]surfaces.area;surfaces.area_symbol" title="Area">A</th>
            <td name="area"></td>
            <td class="surface-err" name="areaError" data-i18n="[title]surfaces.area_error" title="Anticipated area error"></td>
          </tr>
          <tr>
            <th data-i18n="[title]surfaces.perimeter;surfaces.perimeter_symbol" title="Perimeter">P</th>
            <td name="perimeter"></td>
            <td class="surface-err" name="perimeterError" data-i18n="[title]surfaces.perimeter_error" title="Anticipated perimeter error"></td>
          </tr>
          <!-- The volume definition is a hover tooltip on the row, not a line of its own. -->
          <tr class="surface-volume-row" data-i18n="[title]surfaces.volume_note" title="V = plaster over the highlighted points up to the plaster top, parallel to the reference plane, plus A × extra width.">
            <th data-i18n="[title]surfaces.volume;surfaces.volume_symbol" title="Volume">V</th>
            <td name="volume" data-i18n="[title]surfaces.volume_note"></td>
            <td class="surface-err" name="volumeError" data-i18n="[title]surfaces.volume_error" title="Anticipated volume error"></td>
          </tr>
        </table>

        <!-- Grow parameters (angle, distance, gap — a Recalculate each); collapsed by default to save space. -->
        <details class="surface-group" name="growParams">
          <summary class="surface-group-summary" data-i18n="surfaces.grow_params">Detection parameters</summary>
${sliderParamHtml('angleDeg')}

${sliderParamHtml('maxDistance')}

${sliderParamHtml('maxGap')}
        </details>

        <div class="surface-actions">
          <button type="button" class="surface-action-btn" name="recalc" data-i18n="surfaces.recalculate">
            Recalculate
          </button>
          <button type="button" class="surface-action-btn" name="flip" data-i18n="[title]surfaces.flip_title;surfaces.flip" title="Flip the normal direction (swaps hollow / proud)">
            Flip
          </button>
        </div>
        <!-- Display mode, the height-profile controls and the legends; collapsible, open by default. -->
        <details class="surface-group" name="display">
          <summary class="surface-group-summary" data-i18n="surfaces.vis">Display</summary>
        <div class="surface-vis">
          <select name="visMode" class="potree-select" data-i18n="[aria-label]surfaces.vis">
            <option value="${SURFACE_VIS.HIGHLIGHT}" data-i18n="surfaces.vis_highlight">Highlight</option>
            <option value="${SURFACE_VIS.RAINBOW}" data-i18n="surfaces.vis_rainbow">Height profile</option>
            <option value="${SURFACE_VIS.GROWTH}" data-i18n="surfaces.vis_growth">Growth stop</option>
          </select>
        </div>

        <!-- No data-param: this is a select, not a slider (_bindParam must not match it). -->
        <div class="surface-param surface-param--profile" hidden>
          <div class="surface-param-head">
            <label class="pv-select-label" data-i18n="surfaces.profile_ref">Reference plane</label>
            <button type="button" class="surface-param-help" aria-expanded="false" data-i18n="[title]surfaces.help_toggle">?</button>
          </div>
          <p class="surface-param-desc" hidden data-i18n="surfaces.profile_ref_help">
            Colours by distance from an ideally plumb wall (same direction, Z-straight) or a perfectly level floor / ceiling instead of the fitted plane — how much plaster to add or remove for a straight surface. Widen the profile range if a tilted surface saturates. Display only.
          </p>
          <select name="profileRef" class="potree-select" data-i18n="[aria-label]surfaces.profile_ref">
            <option value="${PROFILE_REF.FITTED}" data-i18n="surfaces.profile_ref_fitted">Fitted plane</option>
            <option value="${PROFILE_REF.IDEAL}" data-i18n="surfaces.profile_ref_ideal">Ideal plumb / level</option>
          </select>
        </div>

        <!-- No data-param: this is a select, not a slider (_bindParam must not match it). -->
        <div class="surface-param surface-param--profile" hidden>
          <div class="surface-param-head">
            <label class="pv-select-label" data-i18n="surfaces.profile_bands">Colour resolution</label>
            <button type="button" class="surface-param-help" aria-expanded="false" data-i18n="[title]surfaces.help_toggle">?</button>
          </div>
          <p class="surface-param-desc" hidden data-i18n="surfaces.profile_bands_help">
            Splits the height profile into 2, 4 or 8 colours — half below, half above the zero level — with the boundaries listed in the legend. Display only.
          </p>
          <select name="profileBands" class="potree-select" data-i18n="[aria-label]surfaces.profile_bands">
            ${profileBandsOptionsHtml()}
          </select>
        </div>

${sliderParamHtml('profileOffsetM')}

        <label class="surface-extent-toggle surface-param--profile" hidden data-i18n="[title]surfaces.profile_range_linked_title" title="Checked: one slider drives both ranges, so the colour scale is symmetric about the zero level. Unchecked: set the range below (hollows to fill) and the range above (proud to chip off) separately, each with its own colour scale. Checking it again copies the range below into the range above.">
          <input type="checkbox" name="profileRangeLinked">
          <span data-i18n="surfaces.profile_range_linked">Same range below and above</span>
        </label>

${sliderParamHtml('profileRangeBelowM')}

${sliderParamHtml('profileRangeAboveM')}

        <label class="surface-extent-toggle surface-param--profile" hidden data-i18n="[title]surfaces.highlight_extremes_title" title="Mark the highest and the lowest highlighted point, with the plaster thickness between them">
          <input type="checkbox" name="showHighlightExtremes">
          <span data-i18n="surfaces.highlight_extremes">Mark extreme points</span>
        </label>
        <p class="surface-extent-info" name="highlightExtremesInfo" hidden></p>

        <!-- Plaster: top of the volume and extra width; collapsible, closed by default; hides with the section. -->
        <details class="surface-group surface-param--profile" name="plaster" hidden>
          <summary class="surface-group-summary" data-i18n="surfaces.plaster_group">Plaster</summary>
        <!-- No data-param: this is a select, not a slider (_bindParam must not match it). -->
        <div class="surface-param surface-param--profile" hidden>
          <div class="surface-param-head">
            <label class="pv-select-label" data-i18n="surfaces.volume_top">Plaster top</label>
            <button type="button" class="surface-param-help" aria-expanded="false" data-i18n="[title]surfaces.help_toggle">?</button>
          </div>
          <p class="surface-param-desc" hidden data-i18n="surfaces.volume_top_help">
            Highest highlighted point: nothing is chipped, everything is filled up to the proudest highlighted point. Reference plane: the proud plaster is chipped off and only the hollows are filled, exactly to the zero level.
          </p>
          <select name="volumeTop" class="potree-select" data-i18n="[aria-label]surfaces.volume_top">
            <option value="${VOLUME_TOP.HIGHEST}" data-i18n="surfaces.volume_top_highest">Highest highlighted point</option>
            <option value="${VOLUME_TOP.PLANE}" data-i18n="surfaces.volume_top_plane">Reference plane (chip off the proud)</option>
          </select>
        </div>

${sliderParamHtml('extraPlasterM')}

        </details>

        <div class="surface-scale" name="rainbowScale" hidden>
          <div class="surface-scale-bar" role="img" data-i18n="[aria-label]surfaces.scale_label"></div>
          <div class="surface-scale-ticks">
            <span name="scaleMin"></span>
            <span>0</span>
            <span name="scaleMax"></span>
          </div>
          <div class="surface-scale-captions">
            <span data-i18n="surfaces.scale_hollow">Hollow</span>
            <span data-i18n="surfaces.scale_plane">Level</span>
            <span data-i18n="surfaces.scale_proud">Proud</span>
          </div>
          <div class="surface-band-legend" name="bandLegend" hidden></div>
          <div class="surface-scale-offset" name="scaleOffset" hidden>
            <span data-i18n="surfaces.scale_offset">Zero level: centroid</span>
            <span name="scaleOffsetValue"></span>
          </div>
          <div class="surface-scale-offset" name="scaleRef" hidden>
            <span name="scaleRefWall" data-i18n="surfaces.scale_ref_wall">Reference: ideal plumb wall</span>
            <span name="scaleRefFloor" data-i18n="surfaces.scale_ref_floor" hidden>Reference: ideal level floor / ceiling</span>
            <span data-i18n="surfaces.scale_tilt">· tilt</span> <span name="scaleRefTilt"></span>
          </div>
        </div>
        <div class="surface-stop-legend" name="growthLegend" hidden>
          <div class="surface-stop-row">
            <span class="surface-stop-swatch" data-reason="angle"></span>
            <span data-i18n="surfaces.stop_angle">Angle</span>
          </div>
          <div class="surface-stop-row">
            <span class="surface-stop-swatch" data-reason="distance"></span>
            <span data-i18n="surfaces.stop_distance">Distance</span>
          </div>
          <div class="surface-stop-row">
            <span class="surface-stop-swatch" data-reason="gap"></span>
            <span data-i18n="surfaces.stop_gap">Gap</span>
          </div>
          <div class="surface-stop-row">
            <span class="surface-stop-swatch" data-reason="other"></span>
            <span data-i18n="surfaces.stop_other">Not a slider</span>
          </div>
        </div>
        </details>
        <div style="display:flex; margin-top:12px">
          <span style="flex-grow:1"></span>
          <img name="remove" class="button-icon" src="${removeIconPath}" style="width:16px;height:16px"/>
        </div>
      </div>
    `);

    this.elContent.find('img[name=remove]').click(() => {
      this.viewer.scene.removeSurface(surface);
    });
    this.elContent.find('button[name=recalc]').click(() => {
      if (surface.busy) return;
      this._growNow();
    });
    this.elContent.find('button[name=flip]').click(() => {
      // Plaster side: a profile change, recomputed in the viewer — no grow.
      surface.setFlipped(!surface.flipped);
    });
    this.elContent.find('input[name=showHighlightExtremes]').prop('checked', !!surface.showHighlightExtremes);
    this.elContent.find('input[name=showHighlightExtremes]').on('change', (ev) => {
      // Overlay markers only — never a grow.
      surface.setShowHighlightExtremes(ev.target.checked);
    });
    this.elContent.find('select[name=visMode]').val(surface.visMode || SURFACE_VIS.HIGHLIGHT);
    this.elContent.find('select[name=visMode]').on('change', (ev) => {
      surface.setVisMode(ev.target.value);
      // Growth-stop colours need a frontier table packed at grow time.
      if (surface.visMode === SURFACE_VIS.GROWTH && !(surface.occFrontierCount > 0)) {
        this._markDirty();
      }
      this.update();
    });
    this.elContent.find('select[name=profileRef]').val(surface.profileRef || PROFILE_REF.FITTED);
    this.elContent.find('select[name=profileRef]').on('change', (ev) => {
      // Colouring normal only — shader uniform, never a grow.
      surface.setProfileRef(ev.target.value);
    });
    this.elContent.find('select[name=volumeTop]').val(surface.volumeTop || VOLUME_TOP.HIGHEST);
    this.elContent.find('select[name=volumeTop]').on('change', (ev) => {
      // Volume definition only — a debounced recompute, never a grow.
      surface.setVolumeTop(ev.target.value);
    });
    this.elContent.find('select[name=profileBands]').val(String(surface.profileBands || 0));
    this.elContent.find('select[name=profileBands]').on('change', (ev) => {
      // Colour banding only — shader uniform, never a grow.
      surface.setProfileBands(ev.target.value);
    });

    for (const name of Object.keys(PANEL_GROUPS)) {
      const group = this.elContent.find(`details[name=${name}]`);
      group.prop('open', SurfacePanel.groupOpen(name));
      // Setting `open` above queues a toggle event too; only a real change
      // is stored, so an unchanged default is not frozen into localStorage.
      group.on('toggle', (ev) => {
        const open = ev.currentTarget.open;
        if (open !== SurfacePanel.groupOpen(name)) SurfacePanel.groupOpen(name, open);
      });
    }
    this.elContent.find('.surface-param-help').on('click', (ev) => {
      const btn = ev.currentTarget;
      const wrap = btn.closest('.surface-param');
      const desc = wrap?.querySelector('.surface-param-desc');
      if (!desc) return;
      const open = desc.hasAttribute('hidden');
      if (open) desc.removeAttribute('hidden');
      else desc.setAttribute('hidden', '');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    this._bindParam('angleDeg', 5, 60, 1);
    this._bindParam('maxDistance', 0.5, MAX_DISTANCE_MAX_M * 100, 0.1, { displayScale: 100 });
    this._bindParam('maxGap', 0, 100, 1, { displayScale: 100 });
    // Live client-side volume term (S × w) — no grow / Recalculate.
    this._bindParam('extraPlasterM', 0, EXTRA_PLASTER_MAX_M * 100, 0.1, { displayScale: 100, live: true });
    // Height-profile scale — shader uniforms plus a debounced volume recompute, never a grow.
    this._bindParam('profileOffsetM', -PROFILE_OFFSET_MAX_M * 100, PROFILE_OFFSET_MAX_M * 100, 0.1, {
      displayScale: 100,
      live: true,
      profile: true,
    });
    // Each range follows the grown slab until the user pins it (null → grown).
    // While linked (the default) either slider writes both fields; the other
    // slider catches up through the 'changed' refresh below.
    const linked = () => this.surface.profileRangeLinked !== false;
    this._bindParam('profileRangeBelowM', PROFILE_RANGE_MIN_M * 100, PROFILE_RANGE_MAX_M * 100, 0.1, {
      displayScale: 100,
      live: true,
      profile: true,
      read: () => this.surface.profileRangeBelowEffectiveM(),
      mirror: { key: 'profileRangeAboveM', when: linked },
    });
    this._bindParam('profileRangeAboveM', PROFILE_RANGE_MIN_M * 100, PROFILE_RANGE_MAX_M * 100, 0.1, {
      displayScale: 100,
      live: true,
      profile: true,
      read: () => this.surface.profileRangeAboveEffectiveM(),
      mirror: { key: 'profileRangeBelowM', when: linked },
    });
    this.elContent.find('input[name=profileRangeLinked]').on('change', (ev) => {
      // Display only — never a grow; locking snaps "above" to "below".
      surface.setProfileRangeLinked(ev.target.checked);
    });

    this.propertiesPanel.addVolatileListener(surface, 'changed', this._update);
    // The extremes info line is composed in JS; re-render it on a language switch.
    this.propertiesPanel.addVolatileListener(viewer, 'language_changed', this._update);
    if (typeof this.elContent.i18n === 'function') {
      this.elContent.i18n();
    }
    this.update();
  }

  /**
   * `read` resolves the displayed value when the stored field is not the
   * value itself (a `null` "follow" default). Writes always go to the field,
   * and to `mirror.key` as well while `mirror.when()` holds (linked ranges).
   * Live sliders re-render through the surface's 'changed' listener.
   */
  _bindParam(
    key,
    min,
    max,
    step,
    { displayScale = 1, live = false, profile = false, read = () => this.surface[key], mirror = null } = {}
  ) {
    const wrap = this.elContent.find(`.surface-param[data-param="${key}"]`);
    const sliderEl = wrap.find('[data-slider]');
    const input = wrap.find('.surface-param-input');
    input.attr({ min, max, step });

    const toDisplay = (metresOrValue) => metresOrValue * displayScale;
    const toStored = (display) =>
      displayScale === 1 ? display : Number((display / displayScale).toFixed(6));
    const write = (stored) => {
      this.surface[key] = stored;
      if (mirror && mirror.when()) this.surface[mirror.key] = stored;
    };

    const onChanged = () => {
      if (live) {
        this.surface.dispatchEvent({ type: 'changed', surface: this.surface });
        // Profile sliders move the highlighted set: volume + extremes follow
        // after a short debounce (every drag tick calls this).
        if (profile) this.surface.scheduleVolume();
      } else {
        this._markDirty();
      }
    };

    const apply = (raw) => {
      const next = clampStepped(raw, min, max, step);
      if (next === null) {
        input.val(formatParam(toDisplay(read()), step));
        return false;
      }
      const stored = toStored(next);
      const changed = stored !== this.surface[key] || (mirror && mirror.when() && stored !== this.surface[mirror.key]);
      write(stored);
      sliderEl.slider('value', next);
      input.val(formatParam(next, step));
      if (changed) onChanged();
      return changed;
    };

    sliderEl.slider({
      value: toDisplay(read()),
      min,
      max,
      step,
      slide: (_e, ui) => {
        write(toStored(ui.value));
        input.val(formatParam(ui.value, step));
        onChanged();
      },
      stop: (_e, ui) => apply(ui.value),
    });
    input.val(formatParam(toDisplay(read()), step));
    input.on('change', () => apply(input.val()));
    wrap.find('.surface-step').on('click', (ev) => {
      const dir = Number(ev.currentTarget.getAttribute('data-dir')) || 0;
      apply(toDisplay(read()) + dir * step);
    });
    this._sliders[key] = { sliderEl, input, wrap, step, min, max, displayScale, read };
  }

  _markDirty() {
    this._paramsDirty = true;
    this.elContent.find('button[name=recalc]').toggleClass('is-pending', !this.surface.busy);
  }

  _growNow() {
    if (this.surface.busy) return;
    this._paramsDirty = false;
    this.elContent.find('button[name=recalc]').toggleClass('is-pending', false);
    this.viewer.surfaceTool?.grow(this.surface);
  }

  /**
   * One row per band, proud at the top. Rows are rebuilt only when the band
   * count or a range (cm, 1 decimal) changes, so slider drags do not thrash
   * the DOM. The outer rows run out to −below / +above: beyond them points
   * keep their own colour, so the range edges are real colour boundaries too;
   * a zero side contributes no rows.
   * Swatches sample the gradient the shader samples — the material's current
   * one (the Appearance panel can switch it), SPECTRAL until the cloud is set.
   * Decimals follow the band step so adjacent rows never read the same.
   */
  _renderBandLegend(bands, rangeBelowM, rangeAboveM) {
    const belowCm = rangeBelowM * 100;
    const aboveCm = rangeAboveM * 100;
    const gradient = this.surface.pointcloud?.material?.gradient ?? Gradients.SPECTRAL;
    const key = `${bands}:${belowCm.toFixed(1)}:${aboveCm.toFixed(1)}`;
    if (key === this._bandLegendKey && gradient === this._bandLegendGradient) return;
    this._bandLegendKey = key;
    this._bandLegendGradient = gradient;
    const rows = profileBandLegendRows(rangeBelowM, rangeAboveM, bands).map(({ band, text }) => {
      const c = profileBandColor(gradient, profileBandWeight((band + 0.5) / bands, bands));
      const rgb = [c.r, c.g, c.b].map((v) => Math.round(Math.min(1, Math.max(0, v)) * 255)).join(', ');
      return `<div class="surface-band-row"><span class="surface-band-swatch" style="background: rgb(${rgb})"></span><span class="surface-band-text">${text}</span></div>`;
    });
    this.elContent.find('[name=bandLegend]').html(rows.join(''));
  }

  update() {
    const s = this.surface;
    const fmt = (n, unit, decimals = 2) =>
      Number.isFinite(n) ? `${n.toFixed(decimals)} ${unit}` : '—';
    const fmtErr = (error, total, decimals = 2) => {
      if (!Number.isFinite(error)) return '—';
      const pct = anticipatedErrorPercent(error, total);
      return pct ? `±${error.toFixed(decimals)} (${pct})` : `±${error.toFixed(decimals)}`;
    };
    const vol = plasterVolumeM3(s);
    const volErr = plasterVolumeErrorM3(s);
    // Plaster volumes are small (litres on a wall): three decimals like the
    // surface list, and more when the error would otherwise print as 0.000.
    const volDecimals = Math.max(volumeDecimals(vol), volumeDecimals(volErr));
    this.elContent.find('[name=area]').text(fmt(s.areaM2, 'm²'));
    this.elContent.find('[name=areaError]').text(fmtErr(s.areaErrorM2, s.areaM2));
    this.elContent.find('[name=perimeter]').text(fmt(s.perimeterM, 'm'));
    this.elContent.find('[name=perimeterError]').text(fmtErr(s.perimeterErrorM, s.perimeterM));
    this.elContent.find('[name=volume]').text(fmt(vol, 'm³', volDecimals));
    this.elContent.find('[name=volumeError]').text(fmtErr(volErr, vol, volDecimals));
    this.elContent.find('input[name=showHighlightExtremes]').prop('checked', !!s.showHighlightExtremes);
    const ext = s.highlightExtremes;
    const extInfo = this.elContent.find('[name=highlightExtremesInfo]');
    const inProfile = s.visMode === SURFACE_VIS.RAINBOW;
    extInfo.prop('hidden', !(inProfile && s.showHighlightExtremes && ext));
    if (inProfile && s.showHighlightExtremes && ext) {
      extInfo.text(
        `${surfaceI18n('surfaces.extreme_high', 'Highest')} ${formatExtentCm(ext.highHeightM)} · ` +
          `${surfaceI18n('surfaces.extreme_low', 'Lowest')} ${formatExtentCm(ext.lowHeightM)} · ` +
          `${surfaceI18n('surfaces.extreme_thickness', 'thickness')} ${(ext.thicknessM * 100).toFixed(1)} cm`
      );
    }

    const busy = !!s.busy;
    this.elContent.attr('aria-busy', busy ? 'true' : 'false');
    const recalcBtn = this.elContent.find('button[name=recalc]');
    recalcBtn.prop('disabled', busy);
    recalcBtn.toggleClass('is-pending', !!this._paramsDirty && !busy);
    const visSelect = this.elContent.find('select[name=visMode]');
    visSelect.val(s.visMode || SURFACE_VIS.HIGHLIGHT);
    this.elContent.find('select[name=profileRef]').val(s.profileRef || PROFILE_REF.FITTED);
    this.elContent.find('select[name=volumeTop]').val(s.volumeTop || VOLUME_TOP.HIGHEST);
    this.elContent.find('select[name=profileBands]').val(String(s.profileBands || 0));
    this.elContent.find('input[name=profileRangeLinked]').prop('checked', s.profileRangeLinked !== false);

    for (const ctl of Object.values(this._sliders)) {
      const v = ctl.read();
      if (Number.isFinite(v)) {
        const display = v * ctl.displayScale;
        ctl.sliderEl.slider('value', display);
        if (document.activeElement !== ctl.input[0]) {
          ctl.input.val(formatParam(display, ctl.step));
        }
      }
    }

    const showScale = s.visMode === SURFACE_VIS.RAINBOW;
    const scale = this.elContent.find('[name=rainbowScale]');
    scale.prop('hidden', !showScale);
    this.elContent.find('.surface-param--profile').prop('hidden', !showScale);
    // Linked ranges: one "±" slider stands for both; the "above" slider only
    // appears once they are unlocked. Swapping the data-i18n keys keeps a
    // later language switch correct; the text is set right away.
    const linked = s.profileRangeLinked !== false;
    const belowWrap = this.elContent.find('.surface-param[data-param="profileRangeBelowM"]');
    const labelKey = linked ? 'surfaces.profile_range_both' : 'surfaces.profile_range_below';
    const helpKey = linked ? 'surfaces.profile_range_both_help' : 'surfaces.profile_range_below_help';
    const label = belowWrap.find('.pv-select-label');
    if (label.attr('data-i18n') !== labelKey) {
      label.attr('data-i18n', labelKey).text(surfaceI18n(labelKey, linked ? 'Range ± (cm)' : 'Range below (cm)'));
      belowWrap
        .find('.surface-param-desc')
        .attr('data-i18n', helpKey)
        .text(
          surfaceI18n(
            helpKey,
            linked
              ? 'How far from the zero level, on both sides, the colour gradient reaches. Points beyond keep their original colour and are left out of the volume. Untick "Same range below and above" to set the two sides apart.'
              : 'How deep below the zero level (hollow side, to fill) the colour gradient reaches, with its own scale.'
          )
        );
    }
    this.elContent.find('.surface-param[data-param="profileRangeAboveM"]').prop('hidden', !showScale || linked);
    if (showScale) {
      // The two halves of the bar have their own scales: −below … 0 … +above.
      const rangeBelowM = s.profileRangeBelowEffectiveM();
      const rangeAboveM = s.profileRangeAboveEffectiveM();
      // formatBandEdgeCm keeps a zero side at "0.0", never "−0.0".
      this.elContent.find('[name=scaleMin]').text(`${formatBandEdgeCm(-rangeBelowM * 100)} cm`);
      this.elContent.find('[name=scaleMax]').text(`${formatBandEdgeCm(rangeAboveM * 100)} cm`);
      // Banded resolution swaps the continuous bar for one row per colour.
      const bands = s.profileBands || 0;
      const banded = bands > 1;
      scale.find('.surface-scale-bar, .surface-scale-ticks, .surface-scale-captions').prop('hidden', banded);
      scale.find('[name=bandLegend]').prop('hidden', !banded);
      if (banded) {
        this._renderBandLegend(bands, rangeBelowM, rangeAboveM);
      } else {
        this._bandLegendKey = null;
        this._bandLegendGradient = null;
      }
      // Label is a data-i18n node (re-translated on language switch); only
      // the language-neutral number is written here.
      const offCm = s.profileOffsetM * 100;
      this.elContent.find('[name=scaleOffset]').prop('hidden', Math.abs(offCm) < 0.05);
      this.elContent
        .find('[name=scaleOffsetValue]')
        .text(`${offCm > 0 ? '+' : '−'}${Math.abs(offCm).toFixed(1)} cm`);
      // Reference legend: the shader colours against gpuPlane once packed;
      // before the first pack (occCount 0) the best-known plane is `plane`.
      const plane = s.occCount > 0 ? s.gpuPlane : s.plane;
      const ideal = s.profileRef === PROFILE_REF.IDEAL && Array.isArray(plane?.normal);
      this.elContent.find('[name=scaleRef]').prop('hidden', !ideal);
      if (ideal) {
        const pn = profileNormal(s.profileRef, plane.normal);
        const isLevelPlane = Math.abs(pn[2]) > 0.9; // snapped normal: 0 (wall) or ±1 (floor / ceiling)
        this.elContent.find('[name=scaleRefWall]').prop('hidden', isLevelPlane);
        this.elContent.find('[name=scaleRefFloor]').prop('hidden', !isLevelPlane);
        this.elContent
          .find('[name=scaleRefTilt]')
          .text(`${profileTiltDeg(plane.normal, pn).toFixed(1)}°`);
      }
    }
    this.elContent.find('[name=growthLegend]').prop('hidden', s.visMode !== SURFACE_VIS.GROWTH);
    if (
      s.visMode === SURFACE_VIS.GROWTH &&
      !(s.occFrontierCount > 0) &&
      !s.busy &&
      !this._paramsDirty
    ) {
      this._markDirty();
    }
  }
}
